import { DialogAction } from '../../pages/Proposal/FeeStage/ProposalFeeStage';
import { React, useState, DataGrid, GridColDef, EditCalendarIcon, ContentCopyIcon, Chip, IconButton, useMemo, CustomConfirmDialog, Box, FormControl, Select, MenuItem, Groups2Icon, GridViewRoundedIcon, Tooltip, CustomBackdrop, FeeStageIcon, dayjs, DeleteIcon, useEffect } from '../../utils/globalImports';
import { CustomToggleButton, validationMessages, OverlappedLocationIcon, PersonnelIcon, TagIcon } from '../../utils/globalImports';
import './CustomDataTable.css';

interface Option {
    value: string | number;
    text: string;
}

interface DataTableProps {
    rows: any[];
    actionText: string;
    sortOptions: Option[];
    showEditIcon?: boolean; // Optional prop to control the visibility of the edit icon
    deleteFunction?: boolean;
    onRowEdit: (row: any) => void;  // Callback for editing a row
    onRowCopy?: (row: any) => void;  // Callback for copying a row
    onRowDoubleClick?: (row: any) => void;
    onToggleClick: (row: any) => void;
    onRowDelete?: (row: any) => void;
    isDialogOpen?: boolean;
    isToggleOn?: boolean;
    handleCancel: () => void;
    handleConfirm: () => void;
    gridHeight?: any;
    noResultMessage?: string;
    frameworkMessage?: string;
    customMessage?: string;
    dialogAction?: DialogAction;
    actionbuttonDisable?: boolean;
    customActionTooltip?: string | null;
    customActionIcon?: React.ReactNode;
    onCustomActionClick?: (row: any) => void;
    //clickableColumns?: string[]; // Array of column names that should be clickable links
    //onColumnLinkClick?: (row: any, columnName: string) => void; // Callback for column link clicks

}

const convertToDate = (dateStr: string): Date => {
    const [day, month, year] = dateStr.split("/").map(Number);
    return new Date(year, month - 1, day); // Month is 0-based in JS
};

const getDateDifference = (date1: Date, date2: Date): number => {
    const diffTime = Math.abs(date2.getTime() - date1.getTime()); // Get absolute difference
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
};

const cleanNumber = (val: any): number => {
    if (typeof val === 'string') {
        return Number(val.replace(/[^0-9.-]+/g, '')) || 0;
    }
    return Number(val) || 0;
};

const CustomDataTable: React.FC<DataTableProps> = ({ rows, actionText, sortOptions, showEditIcon = true, deleteFunction, onRowEdit, onRowCopy, onRowDoubleClick, onToggleClick, onRowDelete, isDialogOpen = false, customActionTooltip, isToggleOn, handleCancel, handleConfirm, noResultMessage, frameworkMessage, customMessage, dialogAction, actionbuttonDisable, customActionIcon, onCustomActionClick }) => {

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [sortBy, setSortBy] = useState(sortOptions.length > 0 ? sortOptions[0].value : "");
    const maxLength = 60;
    const maxTooltipLength = 500;
    const gridScrollRef = React.useRef<HTMLDivElement>(null);
    const totalRowRef = React.useRef<HTMLDivElement>(null);
    const currentDate = new Date(new Date().setHours(0, 0, 0, 0)); // Current date

    const includedHeaders = ['Note', 'Fee Stage', 'User', 'Contact', 'Framework', 'Project', 'Parties Involved', 'Proposal', 'Proposal Fee Stage', 'Attachment'];

    const filteredKeys = useMemo(() => {
        const rawKeys = Object.keys(rows[0] || {});
        return rawKeys.filter((_, index) => !(index === 2 && !includedHeaders.includes(actionText)));
    }, [rows, actionText]);

    const PROPOSAL_STICKY_FIELDS = ['actions', 'Fee Stage', 'Sub-Category', 'Description'];

    const PROPOSAL_STICKY_LEFT: Record<string, number> = {
        'actions': 0,
        'Fee Stage': 200,
        'Sub-Category': 400,
        'Description': 600,
    }



    const columns: GridColDef[] = filteredKeys.map((key, index) => ({
        field: key,
        headerName: key.charAt(0).toUpperCase() + key.slice(1),
        // flex: 1,
        ...(actionText === "Proposal Fee Stage"
            ? { width: 200, minWidth: 200 }
            : { flex: 1 }),
        align: 'left',
        headerAlign: 'left',
        ...(actionText === "Proposal Fee Stage" && PROPOSAL_STICKY_FIELDS.includes(key) && {
            cellClassName: `sticky-col sticky-col-${PROPOSAL_STICKY_LEFT[key]}`,
            headerClassName: `sticky-col-header sticky-col-header-${PROPOSAL_STICKY_LEFT[key]}`,
        }),
        renderCell: (params) => {
            const value = params.value || '';

            if (key.toLowerCase() === 'status') {
                const isActive = value === 'Active';
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Chip
                            label={value}
                            size="small"
                            sx={{
                                backgroundColor: isActive ? '#e8f5e9' : '#ffebee',
                                color: isActive ? '#2e7d32' : "#EF4444",
                                fontWeight: '600',
                                fontSize: '11px',
                                border: `1px solid ${isActive ? '#a5d6a7' : '#ef9a9a'}`,
                                height: "24px",
                            }}
                        />
                    </Box>
                );
            }


            const includedToolTip = ['Note', 'Framework', 'Fee Stage', 'Client Category', 'Client Region', 'Fee Stage Area', 'Tags', 'Personnel', 'Project', 'Client', 'Proposal', 'Proposal Fee Stage', 'Atttachment'];

            if (includedToolTip.includes(actionText)) {
                const isLongText = value.length > maxLength;
                const truncatedText = isLongText ? `${value.substring(0, maxLength)}...` : value;
                const tooltipText = (value.length > maxTooltipLength) ? `${value.substring(0, maxTooltipLength)}...` : value;
                return (
                    <Tooltip title={tooltipText} arrow placement="right-start">
                        <div className="data-row" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: 1 }}>
                            {truncatedText}
                        </div>
                    </Tooltip>
                );
            }

            return value;
        },
        renderHeader: (params) => (
            <div className="header">
                {params.colDef.headerName}
            </div>
        ),
    }));


    // const firstColumnField = useMemo(() => Object.keys(rows[0] || [])[0], [rows]);  

    const [firstColumnField, secondColumnField] = useMemo(() => {
        return [filteredKeys[0], filteredKeys[1]];
    }, [filteredKeys]);


    // Dynamically create the columnVisibilityModel
    const columnVisibilityModel = useMemo(() => ({
        [firstColumnField]: false, // Dynamically hide the first column
        [secondColumnField]: false, // Dynamically hide the first column
    }), [firstColumnField, secondColumnField]);

    // const handleRowDoubleClick = (row: any) => {
    //     if (onRowDoubleClick) {
    //         onRowDoubleClick(row);  // Call the passed function on double-click
    //     }
    // };

    // Add actions column dynamically if needed
    const actionsColumn: GridColDef = {
        field: 'actions',
        headerName: 'Actions',
        headerClassName: actionText === "Proposal Fee Stage"
            ? 'header sticky-col-header sticky-col-header-0'
            : 'header',
        cellClassName: actionText === "Proposal Fee Stage"
            ? 'sticky-col sticky-col-0'
            : undefined,
        // width: customActionIcon ? 250 : 150,
        width: actionText === "Proposal Fee Stage" ? 200 : (customActionIcon ? 250 : 150),
        minWidth: actionText === "Proposal Fee Stage" ? 200 : undefined,
        sortable: false,
        disableColumnMenu: true,

        renderCell: (params) => (
            <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left', gap: 1, width: '100%', paddingRight: showEditIcon ? '20%' : '70%', }}>
                {showEditIcon && <IconButton
                    color="primary"
                    aria-label="edit"
                    onClick={() => onRowEdit(params.row)}
                    disabled={actionbuttonDisable}

                >
                    <EditCalendarIcon sx={{ color: '#a3c63d' }} ></EditCalendarIcon>
                </IconButton>}
                {actionText === "Proposal Fee Stage" ? (
                    <>

                        {/* Always show TOGGLE icon */}
                        <CustomToggleButton
                            isOn={params.row.Status === 'Active'}
                            onClick={() => onToggleClick(params.row)}
                            disabled={actionbuttonDisable}
                        />
                        {/* Always show DELETE icon */}
                        <IconButton
                            color="primary"
                            aria-label="delete"
                            onClick={() => onRowDelete?.(params.row)}
                            disabled={actionbuttonDisable}
                        >
                            <DeleteIcon sx={{ color: '#a3c63d' }} />
                        </IconButton>


                    </>
                ) : (
                    deleteFunction ? (
                        <IconButton
                            color="primary"
                            aria-label="delete"
                            onClick={() => onRowDelete?.(params.row)}
                            disabled={actionbuttonDisable}
                        >
                            <DeleteIcon sx={{ color: '#a3c63d' }} />
                        </IconButton>
                    ) : (
                        <CustomToggleButton
                            isOn={params.row.Status === 'Active'}
                            onClick={() => onToggleClick(params.row)}
                            disabled={actionbuttonDisable}
                        />
                    )
                )}
                {(actionText === 'Proposal' || actionText === 'Framework') && (
                    <Tooltip title="Copy">
                        <IconButton
                            color="primary"
                            aria-label="edit"
                            onClick={() => {
                                if (onRowCopy) {
                                    onRowCopy(params.row); // Call the function passed as a prop
                                }
                            }}
                        >
                            <ContentCopyIcon sx={{ color: '#a3c63d' }} ></ContentCopyIcon>
                        </IconButton>
                    </Tooltip>
                )}

                {/*Additional Custom action icon if provided */}
                {customActionIcon && onCustomActionClick && (
                    customActionTooltip ? (
                        <Tooltip title={customActionTooltip} arrow>
                            <IconButton
                                color="primary"
                                aria-label="custom-action"
                                onClick={() => onCustomActionClick?.(params.row)}
                                disabled={actionbuttonDisable}
                            >
                                {customActionIcon}
                            </IconButton>

                        </Tooltip>
                    ) : (
                        <IconButton
                            color="primary"
                            aria-label="custom-action"
                            onClick={() => onCustomActionClick?.(params.row)}
                            disabled={actionbuttonDisable}
                        >
                            {customActionIcon}
                        </IconButton>
                    )
                )}
            </div>
        ),

    };
    // Conditionally add the actions column at the start or end
    if (actionText === "Proposal Fee Stage") {
        columns.unshift(actionsColumn); // add at the start
    } else {
        columns.push(actionsColumn); // add at the end
    }

    const sortedRows = [...rows].sort((a, b) => {
        const sortKey = String(sortBy);
        const parts = sortKey.split("_");
        const field = parts[0]; // e.g., "projectName", "Created date"
        const direction = parts[1] ?? "asc"; // "asc" if no direction provided

        // Special case: date field
        if (field === "Created date" || field === "Upload Date") {
            const aDate = dayjs(a[field], "DD/MM/YYYY HH:mm:ss").valueOf();
            const bDate = dayjs(b[field], "DD/MM/YYYY HH:mm:ss").valueOf();

            return direction === "asc" ? aDate - bDate : bDate - aDate;
        }

        // Default string/numeric comparison
        const aValue = a[field as keyof typeof a] ?? "";
        const bValue = b[field as keyof typeof a] ?? "";



        const result = String(aValue).localeCompare(
            String(bValue),
            undefined,
            { numeric: true, sensitivity: "base" }
        );

        return direction === "asc" ? result : -result;
    });

    const paginatedRows = sortedRows.slice(page * pageSize, (page + 1) * pageSize);
    const totalEntries = rows.length;
    const startEntry = page * pageSize + 1;
    const endEntry = Math.min((page + 1) * pageSize, totalEntries);
    const totalPages = Math.ceil(totalEntries / pageSize);

   useEffect(() => {
    if (actionText !== 'Proposal Fee Stage') return;

    const totalRow = totalRowRef.current;
    if (!totalRow) return;

    let scroller: HTMLDivElement | null = null;
    let rafId: number;

    const onScroll = () => {
        if (scroller) totalRow.scrollLeft = scroller.scrollLeft;
    };

    const attach = () => {
        scroller = gridScrollRef.current?.querySelector('.MuiDataGrid-virtualScroller') as HTMLDivElement;
        if (scroller) {
            scroller.addEventListener('scroll', onScroll);
        } else {
            rafId = requestAnimationFrame(attach);
        }
    };

    attach();

    return () => {
        cancelAnimationFrame(rafId);
        scroller?.removeEventListener('scroll', onScroll);
    };
}, [actionText, page, pageSize, rows.length]);


    const totalProposalValue = paginatedRows.reduce((sum, row) => {
        return sum + cleanNumber(row["Proposal Value"]);
    }, 0);

    const totalInstructedValue = paginatedRows.reduce((sum, row) => {
        return sum + cleanNumber(row["Instructed Value"])
    }, 0);

    const totalUnInstructedValue = paginatedRows.reduce((sum, row) => {
        return sum + cleanNumber(row["Uninstructed Value"])
    }, 0);

    // const totalQuantity = paginatedRows.reduce((sum, row) => sum + (row.Quantity || 0), 0);
    // const totalProposalValue = paginatedRows.reduce((sum, row) => sum + (row["Proposal Value"] || 0), 0);
    // const totalInstructedValue = paginatedRows.reduce((sum, row) => sum + (row["Instructed Value"] || 0), 0);
    // const totalUnInstructedValue = paginatedRows.reduce((sum, row) => sum + (row["Uninstructed Value"] || 0), 0);

    const getActionVerb = () => {
        // Customize verbs based on actionText
        if (actionText === 'attachment') return 'delete';
        return isToggleOn ? 'deactivate' : 'activate';
    };

    return (
        <>
            <Box sx={{
                flex: 1,
                display: 'flex',
                minHeight: 0,
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <Box display="flex" justifyContent="space-between" mb={2} mt={2} alignItems="left" flexWrap="wrap">
                    <Box display="flex" alignItems="left" mb={1}>
                        <span style={{ color: '#1e66ab' }}>Show</span>
                        <FormControl size="small" sx={{ mx: 1, minWidth: 70 }}>
                            <Select
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                sx={{
                                    fontSize: '0.75rem', // Smaller font size
                                    color: 'rgba(30, 102, 171, 0.7)',
                                    height: '30px', // Reduce height
                                    '.MuiSelect-select': {
                                        padding: '4px 8px', // Adjust padding
                                        minHeight: 'unset', // Prevents default Material-UI height
                                    },
                                }}
                            >
                                {[10, 20, 50, 100].map(size => (
                                    <MenuItem key={size} value={size}>{size}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <span style={{ color: '#1e66ab' }}>entries</span>
                    </Box>
                    <Box display="flex" alignItems="left" mb={1}>
                        <span style={{ color: '#1e66ab' }}>Sort by</span>
                        <FormControl size="small" sx={{ ml: 1, minWidth: 100, height: '30px', }}>
                            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                                sx={{
                                    fontSize: '0.75rem', // Smaller font size
                                    color: 'rgba(30, 102, 171, 0.7)',
                                    height: '30px', // Reduce height
                                    '.MuiSelect-select': {
                                        padding: '4px 8px', // Adjust padding
                                        minHeight: 'unset', // Prevent default height from Material-UI
                                    },
                                }}>
                                {sortOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    flexShrink: 1,
                    minHeight: 0,
                    width: '100%',
                    height: '100%',
                    bgcolor: 'white',
                    overflow: 'hidden'
                }}>
                    {paginatedRows.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <span style={{ color: "#1e66ab", fontSize: "14px", fontWeight: 500 }}>{noResultMessage || 'No data'}</span>
                        </Box>
                    ) : (
                        <Box sx={{ flex: 1, minHeight: 0, flexDirection: "column", display: "flex", overflow: "hidden" }}>
                            <Box ref={gridScrollRef} sx={{ flex: 1, minHeight: 0, overflow: 'hidden', }}>
                                <DataGrid
                                    rows={paginatedRows}
                                    columns={columns}
                                    hideFooterSelectedRowCount
                                    hideFooterPagination
                                    // disableColumnSorting
                                    // disableColumnMenu
                                    columnVisibilityModel={
                                        // Hide columns status and traderName, the other columns will remain visible
                                        columnVisibilityModel
                                    }
                                    getRowId={(row) => row[firstColumnField]}
                                    getRowHeight={() => 'auto'}
                                    getRowClassName={(params) => {

                                        // Apply red row if actionText is Proposal Fee Stage or FeeStage and any value is negative
                                        if (actionText === "Proposal Fee Stage" || actionText === "FeeStage") {
                                            const columnKey = actionText === "FeeStage" ? "value" : "Unit Price";
                                            const cellValue = params.row[columnKey];

                                            if (typeof cellValue === 'string') {
                                                const numericValue = parseFloat(cellValue.replace(/[^-0-9.]/g, ''));
                                                if (!isNaN(numericValue) && numericValue < 0) {
                                                    return "row-with-negative-value";
                                                }
                                            } else if (typeof cellValue === 'number' && cellValue < 0) {
                                                return "row-with-negative-value";
                                            }
                                        }

                                        // Apply red border ONLY if actionText is 'framework' AND endDate is today
                                        if (actionText === "Framework") {
                                            const endDate = convertToDate(params.row["End Date"]);
                                            if (endDate <= currentDate)
                                                return "expired-framework-row";
                                            else {
                                                const dateDifference = getDateDifference(currentDate, endDate);
                                                if (dateDifference <= 89)
                                                    return "ToBeExpire-framework-row";
                                                else return ""; // No styling for other rows
                                            }
                                        }
                                        return ""; // No styling for other rows
                                    }}
                                    onRowDoubleClick={(params) => {
                                        if (onRowDoubleClick) {
                                            onRowDoubleClick(params.row); // Call the function passed as a prop
                                        }
                                    }}
                                    sx={{
                                        flex: 1,
                                        height: "100%",
                                        width: "100%",
                                        // overflowX: "auto",
                                        // minWidth: actionText === 'Proposal Fee Stage' ? '3500px' : '100%',

                                       
                                        '& .MuiDataGrid-cell': {
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            padding: '8px',
                                            justifyContent: 'left',
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                       
                                        '& .MuiDataGrid-cell:focus': { outline: 'none' }, // Remove the focus outline
                                        '& .MuiDataGrid-virtualScroller': { overflowX: "auto", overflowY: 'auto' },
                                        '& .MuiDataGrid-virtualScrollerContent': { minWidth: actionText === "Proposal Fee Stage" ? "3500px" : "0px" },
                                        '& .MuiDataGrid-row': {
                                            borderLeft: '15px solid transparent',
                                            backgroundColor: 'white',
                                            position: 'relative',
                                            boxSizing: 'border-box',
                                            marginLeft: '-0.4%',
                                            cursor: 'pointer',
                                        },
                                        "& .MuiDataGrid-row:hover": {
                                            backgroundColor: "#f0f4ff",
                                            borderLeft: "15px solid #1e66ab33",
                                        },
                                        '& .Mui-selected': {
                                            backgroundColor: 'rgba(169, 201, 81, 0.14) !important',
                                            color: '#96c121',
                                            boxShadow: 'none',
                                        },
                                        '& .Mui-selected .MuiDataGrid-cell': {
                                            color: '#96c121',
                                        },
                                        '& .MuiDataGrid-row.Mui-selected': {
                                            borderLeft: '15px solid #96c121',
                                        },
                                        "& .MuiDataGrid-columnHeaders": {
                                            borderBottom: "2px solid #e0e0e0",
                                            backgroundColor: "#f5f8ff",
                                        },
                                        '& .MuiDataGrid-columnHeader': {
                                            paddingLeft: actionText === 'Proposal Fee Stage' ? '7px' : '15 px',
                                            borderRight: 'none !important',
                                            fontWeight: '450',
                                            fontSize: '16px',
                                            justifyContent: 'left',
                                            display: 'flex',
                                            alignItems: 'left',
                                            textAlign: 'left',
                                        },
                                        '& .MuiDataGrid-columnSeparator': {
                                            display: 'none !important',
                                        },
                                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                                            outline: 'none',
                                        },
                                        '& .MuiDataGrid-cell, & .MuiDataGrid-footerContainer': {
                                            color: '#1e66ab',
                                        },
                                        "& .MuiDataGrid-footerContainer": {
                                            color: "#1e66ab",
                                            borderTop: "2px solid #e0e0e0",
                                            backgroundColor: "#f5f8ff",
                                            minHeight: "52px",
                                        },
                                        // Style only for rows where actionText is 'framework' AND endDate is today
                                        '& .expired-framework-row': {
                                            borderLeft: '15px solid red', // Red border for expired framework rows
                                            backgroundColor: '#ffe6e6', // Light red background
                                        },
                                        '& .ToBeExpire-framework-row': {
                                            borderLeft: '15px solid #FC6A21', // Amber border for expired framework rows
                                            backgroundColor: '#FFD580', // Amber background
                                        },
                                        '& .row-with-negative-value': {
                                            borderLeft: '15px solid red',
                                            backgroundColor: '#fdecea', // Light red background
                                        },
                                        '& .data-row': {
                                            //paddingTop: '10px',
                                        },
                                         // Proposal fee Stage Table 
                                          ...(actionText === "Proposal Fee Stage" && {

                                            // Sticky cells
                                            '& .sticky-col-0': { position: 'sticky', left: '0px', zIndex: 4, backgroundColor: 'white' },
                                            '& .sticky-col-200': { position: 'sticky', left: '200px', zIndex: 4, backgroundColor: 'white' },
                                            '& .sticky-col-400': { position: 'sticky', left: '400px', zIndex: 4, backgroundColor: 'white' },
                                            '& .sticky-col-600': { position: 'sticky', left: '600px', zIndex: 4, backgroundColor: 'white' },

                                            // Sticky headers
                                            '& .sticky-col-header-0': { position: 'sticky', left: '0px', zIndex: 6, backgroundColor: '#fff', pl: "10px" },
                                            '& .sticky-col-header-200': { position: 'sticky', left: '200px', zIndex: 6, backgroundColor: '#fff' },
                                            '& .sticky-col-header-400': { position: 'sticky', left: '400px', zIndex: 6, backgroundColor: '#fff' },
                                            '& .sticky-col-header-600': { position: 'sticky', left: '600px', zIndex: 6, backgroundColor: '#fff' },
                                            '& .MuiDataGrid-row': {
                                                backgroundColor: 'transparent',
                                                borderLeft: '15px solid transparent',
                                                marginLeft: '-0.2%',
                                                cursor: 'pointer',
                                            },
                                            '& .MuiDataGrid-row .MuiDataGrid-cell:not(.sticky-col)': {
                                                backgroundColor: 'white',
                                            },
                                            '& .MuiDataGrid-row:hover .MuiDataGrid-cell:not(.sticky-col)': {
                                                backgroundColor: '#f0f4ff',
                                            },
                                            '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell:not(.sticky-col)': {
                                                backgroundColor: '#f3f8e9',
                                            },
                                            '& .MuiDataGrid-row .sticky-col': {
                                                backgroundColor: 'white !important',
                                            },
                                            '& .MuiDataGrid-row:hover .sticky-col': {
                                                backgroundColor: '#f0f4ff !important',
                                            },
                                            '& .MuiDataGrid-row.Mui-selected .sticky-col': {
                                                backgroundColor: '#f3f8e9 !important',
                                            },
                                            '& .row-with-negative-value .MuiDataGrid-cell:not(.sticky-col)': {
                                                backgroundColor: '#fdecea',
                                            },
                                            '& .row-with-negative-value .sticky-col': {
                                                backgroundColor: '#fdecea !important',
                                            },
                                        }),

                                    }}
                                />
                            </Box>
                            {actionText === 'Proposal Fee Stage' && (
                                <Box
                                    ref={totalRowRef}
                                    sx={{
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        '&::-webkit-scrollbar': { display: 'none' },
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        backgroundColor: '#fff',
                                        borderTop: '2px solid #e0e0e0',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontWeight: 'bold',
                                        color: '#1E66AB',
                                        minWidth: '3500px',
                                        minHeight: '52px',
                                    }}>
                                        {/* Sticky Total label */}
                                        <div style={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 4,
                                            backgroundColor: '#fff',
                                            minWidth: '815px',
                                            padding: '12px 8px',
                                            flexShrink: 0,
                                        }}>
                                            Total
                                        </div>

                                        <div style={{ minWidth: '200px', flexShrink: 0 }} />
                                        <div style={{ minWidth: '200px', flexShrink: 0 }} />
                                        <div style={{ minWidth: '200px', flexShrink: 0, padding: '12px 8px' }}>
                                            {totalProposalValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                                        </div>
                                        <div style={{ minWidth: '200px', flexShrink: 0, padding: '12px 8px' }}>
                                            {totalInstructedValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                                        </div>
                                        <div style={{ minWidth: '200px', flexShrink: 0, padding: '12px 8px' }}>
                                            {totalUnInstructedValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                                        </div>
                                    </div>
                                </Box>
                            )}
                        </Box>

                    )}
                </Box>

                <Box display="flex" justifyContent="space-between" mt={2} alignItems="left" flexWrap="wrap">
                    <span style={{ color: '#1e66ab' }}>
                        Showing {startEntry} to {endEntry} of {totalEntries} entries
                    </span>
                    <Box display="flex" flexWrap="wrap" mt={1} border="1px solid #ccc" borderRadius={1} overflow="hidden">
                        {/* Page dropdown */}
                        <FormControl size="small" sx={{ mx: 0.5, minWidth: 40, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}>
                            <Select
                                value={page}
                                onChange={(e) => setPage(Number(e.target.value))}
                                sx={{ fontSize: '0.875rem', color: '#1e66ab', '&:hover': { bgcolor: '#f0f0f0' } }}
                            >
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <MenuItem key={i} value={i} sx={{ fontSize: '0.875rem' }}>{i + 1}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {/* Previous Button */}
                        <button
                            style={{ border: '1px solid #ddd', padding: '5px 10px', cursor: 'pointer', backgroundColor: 'white', color: '#1e66ab', fontFamily: 'inherit' }}
                            disabled={page === 0}
                            onClick={() => setPage(page - 1)}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f2f2f2'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                            Previous
                        </button>

                        {/* Page Numbers with Ellipsis */}
                        {(() => {
                            const totalPages = Math.ceil(totalEntries / pageSize);
                            const visiblePages = 5; // Show 5 pages at a time
                            let startPage = Math.max(0, page - Math.floor(visiblePages / 2));
                            let endPage = Math.min(totalPages - 1, startPage + visiblePages - 1);

                            if (endPage - startPage < visiblePages - 1) {
                                startPage = Math.max(0, endPage - visiblePages + 1);
                            }

                            const pages = [];

                            if (startPage > 0) {
                                pages.push(
                                    <button
                                        key={0}
                                        onClick={() => setPage(0)}
                                        style={{ border: '1px solid #ddd', padding: '5px 10px', backgroundColor: page === 0 ? '#1e66ab' : 'white', color: page === 0 ? 'white' : '#1e66ab', cursor: 'pointer', fontFamily: 'inherit' }}
                                    >
                                        1
                                    </button>
                                );
                                if (startPage > 1) pages.push(<span key="left-ellipsis" style={{ padding: '5px 10px' }}>...</span>);
                            }

                            for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                    <button
                                        key={i}
                                        onClick={() => setPage(i)}
                                        style={{ border: '1px solid #ddd', padding: '5px 10px', backgroundColor: page === i ? '#1e66ab' : 'white', color: page === i ? 'white' : '#1e66ab', cursor: 'pointer', fontFamily: 'inherit' }}
                                    >
                                        {i + 1}
                                    </button>
                                );
                            }

                            if (endPage < totalPages - 1) {
                                if (endPage < totalPages - 2) pages.push(<span key="right-ellipsis" style={{ padding: '5px 10px' }}>...</span>);
                                pages.push(
                                    <button
                                        key={totalPages - 1}
                                        onClick={() => setPage(totalPages - 1)}
                                        style={{ border: '1px solid #ddd', padding: '5px 10px', backgroundColor: page === totalPages - 1 ? '#1e66ab' : 'white', color: page === totalPages - 1 ? 'white' : '#1e66ab', cursor: 'pointer', fontFamily: 'inherit' }}
                                    >
                                        {totalPages}
                                    </button>
                                );
                            }

                            return pages;
                        })()}

                        {/* Next Button */}
                        <button
                            style={{ border: '1px solid #ddd', padding: '5px 10px', cursor: 'pointer', backgroundColor: 'white', color: '#1e66ab', fontFamily: 'inherit' }}
                            disabled={page >= Math.ceil(totalEntries / pageSize) - 1}
                            onClick={() => setPage(page + 1)}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f2f2f2'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                            Next
                        </button>
                    </Box>
                </Box>

            </Box>

            <CustomConfirmDialog
                open={isDialogOpen}
                title={dialogAction === "delete"
                    ? `Delete ${actionText}` : validationMessages.common_toggletitle(
                        getActionVerb().charAt(0).toUpperCase() + getActionVerb().slice(1), // Capitalize
                        actionText
                    )}
                message={
                    customMessage                // 1. If delete message → use it
                        ? customMessage
                        : frameworkMessage
                            ? frameworkMessage
                            : validationMessages.common_toggleConfirm(
                                getActionVerb(),
                                actionText
                            )
                }
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                onClose={handleCancel}
            />
        </>
    );
};

export default CustomDataTable;
